import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { format } from 'date-fns';
import LoadingSpinner from '../shared/components/LoadingSpinner';
import { courtApi, ppuApi } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { PublicFacility, Court } from '../api_types';
import NewMemberCourtReservations from './MemberCourtReservations';
import { useNavigate, useLocation } from 'react-router-dom';

// Time utility functions
const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${minutes} ${period}`;
};

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (paymentMethod: 'online' | 'onsite') => void;
  price: number;
  businessId: string;
  facility: PublicFacility;
  date: string;
  startTime: string;
  endTime: string;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({ 
  open, 
  onClose, 
  onConfirm, 
  price, 
  businessId,
  facility,
  date,
  startTime,
  endTime
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Complete Your Reservation</DialogTitle>
      <DialogContent>
        <div className="p-4">
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Reservation Details</h3>
            <div className="space-y-2 text-gray-600">
              <p><strong>Facility:</strong> {facility.name}</p>
              <p><strong>Date:</strong> {format(new Date(date + 'T00:00:00'), 'MMMM d, yyyy')}</p>
              <p><strong>Time:</strong> {formatTime(startTime)} - {formatTime(endTime)}</p>
              <p><strong>Total Amount:</strong> ${(price / 100).toFixed(2)}</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <button
              onClick={() => onConfirm('online')}
              className="w-full py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Pay Now Online
            </button>
          </div>
          
          <div className="mt-6 text-sm text-gray-500">
            <p>* Online payment requires immediate processing</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface TimeSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (startTime: string, endTime: string) => void;
  initialStartTime: string;
  initialEndTime: string;
  courtName?: string;
}

const TimeSelectionDialog: React.FC<TimeSelectionDialogProps> = ({ 
  open, 
  onClose, 
  onSubmit,
  initialStartTime,
  initialEndTime,
  courtName
}) => {
  const [startTime, setStartTime] = useState(initialStartTime);
  const [endTime, setEndTime] = useState(initialEndTime);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [duration, setDuration] = useState(1); // Default 1 hour

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStartTime(initialStartTime);
      setEndTime(initialEndTime);
      setDuration(1);
    }
  }, [open, initialStartTime, initialEndTime]);

  // Generate time slots from 8 AM to 9 PM in 30-minute intervals
  const TIME_SLOTS = useMemo(() => {
    const slots = [];
    for (let hour = 8; hour <= 21; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push({
          display: formatTime(time),
          value: time
        });
      }
    }
    return slots;
  }, []);

  // Update end time whenever start time or duration changes
  useEffect(() => {
    if (startTime) {
      const [hours, minutes] = startTime.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes + (duration * 60);
      const endHour = Math.floor(totalMinutes / 60) % 24;
      const endMinute = totalMinutes % 60;
      const newEndTime = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;
      setEndTime(newEndTime);
    }
  }, [startTime, duration]);

  const handleClose = () => {
    setShowTimeDropdown(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <h2 className="text-2xl font-normal">Select start time</h2>
      </DialogTitle>
      <DialogContent>
        <div className="p-4 space-y-6">
          {courtName && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Court
              </label>
              <div className="w-full p-3 text-lg border rounded-lg bg-gray-50 text-gray-700">
                {courtName}
              </div>
            </div>
          )}

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Time
            </label>
            <div 
              onClick={() => setShowTimeDropdown(!showTimeDropdown)}
              className="w-full p-3 text-lg border rounded-lg bg-white hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
            >
              {startTime ? formatTime(startTime) : 'Select time'}
            </div>

            {showTimeDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-96 overflow-auto">
                {TIME_SLOTS.map((slot) => (
                  <div
                    key={slot.value}
                    onClick={() => {
                      setStartTime(slot.value);
                      setShowTimeDropdown(false);
                    }}
                    className={`p-3 text-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                      startTime === slot.value ? 'bg-indigo-50 text-indigo-600' : ''
                    }`}
                  >
                    {slot.display}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-lg font-medium text-gray-900">
              Duration
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[0.5, 1].map((durationOption) => (
                <button
                  key={durationOption}
                  onClick={() => setDuration(durationOption)}
                  className={`p-3 text-lg rounded-lg transition-colors ${
                    duration === durationOption
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  {durationOption === 0.5 ? '30 min' : '1 hour'}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-lg font-medium text-gray-900">
              End Time
            </label>
            <div className="p-3 text-lg bg-gray-100 rounded-lg">
              {endTime ? formatTime(endTime) : '-'}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={handleClose}
              className="px-6 py-2.5 text-lg border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onSubmit(startTime, endTime)}
              className="px-6 py-2.5 text-lg bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              disabled={!startTime || !endTime}
            >
              Create
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface PPU_ReservationProps {
  facilityId: string;
  businessId: string;
  facility: PublicFacility;
}

const PPU_Reservation: React.FC<PPU_ReservationProps> = ({ facilityId, businessId, facility }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showTimeSelectionDialog, setShowTimeSelectionDialog] = useState(false);
  const [pendingReservation, setPendingReservation] = useState<any>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{ courtId: string; time: string } | null>(null);
  const [selectedCourt, setSelectedCourt] = useState<{ id: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [courts, setCourts] = useState<Court[]>([]);
  const [checkingPaymentStatus, setCheckingPaymentStatus] = useState(false);

  // Check for booking_id in URL params when component mounts or URL changes
  useEffect(() => {
    const checkPaymentStatus = async () => {
      const params = new URLSearchParams(location.search);
      const bookingId = params.get('booking_id');
      const urlBusinessId = params.get('business_id');
      const urlFacilityId = params.get('facility_id');
      
      if (bookingId && urlBusinessId && urlFacilityId) {
        try {
          setCheckingPaymentStatus(true);
          const status = await ppuApi.getBookingStatus(bookingId, urlBusinessId, urlFacilityId);
          
          if (status.status === 'confirmed') {
            // Clear URL parameters
            window.history.replaceState({}, document.title, window.location.pathname);
            // Show success message or redirect
            navigate('/member/dashboard/reservations', { 
              state: { 
                message: 'Reservation confirmed successfully!',
                type: 'success'
              }
            });
          } else if (status.status === 'pending') {
            // If still pending, redirect to payment URL
            if (status.paymentUrl) {
              window.location.href = status.paymentUrl;
            }
          }
        } catch (error: any) {
          console.error('Error checking payment status:', error);
          setError('Failed to check payment status. Please try again.');
        } finally {
          setCheckingPaymentStatus(false);
        }
      }
    };

    checkPaymentStatus();
  }, [location.search, navigate]);

  // Fetch courts when component mounts
  useEffect(() => {
    const fetchCourts = async () => {
      try {
        const response = await courtApi.getCourts(businessId, facilityId);
        setCourts(response);
      } catch (err) {
        console.error('Error fetching courts:', err);
        setError('Failed to load courts');
      } finally {
        setLoading(false);
      }
    };

    fetchCourts();
  }, [facilityId]);

  const handleTimeSlotSelect = (timeSlot: { courtId: string; time: string }) => {
    // Calculate price based on whether it's a weekend and peak hours
    const date = new Date();
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const time = timeSlot.time;
    const [hours, minutes] = time.split(':').map(Number);
    const slotTime = hours + minutes / 60;
    
    const peakStart = parseInt(facility.publicPricing.peakHours.startTime.split(':')[0]);
    const peakEnd = parseInt(facility.publicPricing.peakHours.endTime.split(':')[0]);
    const isPeakHour = !isWeekend && slotTime >= peakStart && slotTime < peakEnd;
    
    let price = 0;
    if (isWeekend) {
      price = facility.publicPricing.weekendRate;
    } else if (isPeakHour) {
      price = facility.publicPricing.weekdayPeakRate;
    } else {
      price = facility.publicPricing.weekdayOffPeakRate;
    }

    handleReservationAttempt({
      courtId: timeSlot.courtId,
      date: format(new Date(), 'yyyy-MM-dd'),
      startTime: timeSlot.time,
      endTime: '',
      price
    });
  };

  const handleReservationAttempt = (reservationData: {
    courtId: string;
    date: string;
    startTime: string;
    endTime: string;
    price: number;
  }) => {
    const court = courts.find((c: Court) => c.id === reservationData.courtId);
    setSelectedCourt(court ? { id: court.id, name: court.name } : null);
    setSelectedTimeSlot({ courtId: reservationData.courtId, time: reservationData.startTime });
    setPendingReservation(reservationData);
    setShowTimeSelectionDialog(true);
  };

  const handleTimeSelection = (startTime: string, endTime: string) => {
    if (pendingReservation) {
      // Calculate duration in hours (fractional)
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);
      let duration = (endHour + endMinute / 60) - (startHour + startMinute / 60);
      // Restrict to 0.5 or 1 hour only
      if (duration !== 0.5 && duration !== 1) {
        duration = 1; // fallback to 1 hour if something goes wrong
      }

      // Calculate price based on whether it's a weekend and peak hours
      const date = new Date(pendingReservation.date + 'T00:00:00');
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const slotTime = startHour + startMinute / 60;
      
      const peakStart = parseInt(facility.publicPricing.peakHours.startTime.split(':')[0]);
      const peakEnd = parseInt(facility.publicPricing.peakHours.endTime.split(':')[0]);
      const isPeakHour = !isWeekend && slotTime >= peakStart && slotTime < peakEnd;
      
      let hourlyRate = 0;
      if (isWeekend) {
        hourlyRate = facility.publicPricing.weekendRate;
      } else if (isPeakHour) {
        hourlyRate = facility.publicPricing.weekdayPeakRate;
      } else {
        hourlyRate = facility.publicPricing.weekdayOffPeakRate;
      }

      const price = Math.round(hourlyRate * duration); // in cents
      setPendingReservation({
        ...pendingReservation,
        startTime,
        endTime,
        price
      });
      setShowTimeSelectionDialog(false);
      setShowPaymentDialog(true);
    }
  };

  const handlePaymentMethod = async () => {
    try {
      setError(null);
      
      const bookingData = {
        businessId,
        facilityId,
        courtId: pendingReservation.courtId,
        date: pendingReservation.date,
        startTime: pendingReservation.startTime,
        endTime: pendingReservation.endTime,
        price: pendingReservation.price,
        paymentMethod: 'online',
        guestDetails: {
          name: user?.name || user?.email || 'Guest',
          email: user?.email || '',
          phone: user?.phone || ''
        }
      };

      const response = await ppuApi.createBooking(bookingData);
      
      if (response.paymentUrl) {
        window.location.href = response.paymentUrl;
      } else {
        throw new Error('No payment URL received');
      }
      
      setShowPaymentDialog(false);
      setPendingReservation(null);
    } catch (error: any) {
      console.error('Error creating reservation:', error);
      setError(error.message || 'Failed to create reservation. Please try again.');
    }
  };

  if (loading || checkingPaymentStatus) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <NewMemberCourtReservations
        businessId={businessId}
        facilityId={facilityId}
        onReservationAttempt={handleReservationAttempt}
        facility={facility}
        disableNewReservation={true}
        isGuestMode={true}
        defaultStartTime={selectedTimeSlot?.time}
        onTimeSlotSelect={handleTimeSlotSelect}
        forceShowGrid={true}
      />

      <TimeSelectionDialog
        open={showTimeSelectionDialog}
        onClose={() => {
          setShowTimeSelectionDialog(false);
          setSelectedTimeSlot(null);
          setSelectedCourt(null);
        }}
        onSubmit={handleTimeSelection}
        initialStartTime={selectedTimeSlot?.time || ''}
        initialEndTime={''}
        courtName={selectedCourt?.name}
      />

      {error && (
        <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {pendingReservation && (
        <PaymentDialog
          open={showPaymentDialog}
          onClose={() => setShowPaymentDialog(false)}
          onConfirm={handlePaymentMethod}
          price={pendingReservation.price}
          businessId={businessId}
          facility={facility}
          date={pendingReservation.date}
          startTime={pendingReservation.startTime}
          endTime={pendingReservation.endTime}
        />
      )}
    </>
  );
};

export default PPU_Reservation; 